/* ============================================================
   LIORA - Motion fuer die Startseite
   Wird nur auf der Startseite geladen (layout/theme.liquid).

   - Scroll-Reveals fuer Ueberschriften, Karten, Atelier, Stoff-Rad
   - Parallax auf dem Atelier-Bild
   - Automatisches Weiterdrehen des Stoff-Rads
   - Hero: Headline Wort fuer Wort, rotierendes Atelier-Monogramm
   - Soft-Club-Marquee reagiert auf die Scroll-Geschwindigkeit
   - Atelier-Text bekommt eine Naht, die sich beim Scrollen schliesst
   - Newsletter: Poststempel aus Heidelberg zeichnet sich

   Im Theme-Editor und bei 'Bewegung reduzieren' passiert nichts.
   Inhalte bleiben ohne JavaScript immer sichtbar.
   ============================================================ */
(() => {
  'use strict';

  const root = document.documentElement;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (root.classList.contains('shopify-design-mode')) return;
  if (!('IntersectionObserver' in window)) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  const CONFIG = {
    stagger: 90,
    maxStaggerSteps: 5,
    parallax: true,
    wheelAutoplay: true,
    wheelInterval: 4200,
    wheelPauseAfterInteraction: 9000,
    marqueeBoost: 0.045,
    marqueeMaxRate: 5,
  };

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const svg = (markup, className) => {
    const template = document.createElement('template');
    template.innerHTML = markup.trim();
    const node = template.content.firstElementChild;
    if (className) node.setAttribute('class', className);
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('focusable', 'false');
    return node;
  };

  /* ----- 0. Hero: Headline Wort fuer Wort ----- */

  const heroHeading = main.querySelector('.hero__content-wrapper h1');
  const heroHost = heroHeading && heroHeading.closest('.hero__content-wrapper > *');

  if (heroHeading && heroHost && heroHeading.children.length === 0) {
    const words = heroHeading.textContent.trim().split(/\s+/);
    heroHeading.setAttribute('aria-label', heroHeading.textContent.trim());
    heroHeading.textContent = '';
    words.forEach((word, index) => {
      const outer = document.createElement('span');
      const inner = document.createElement('span');
      outer.className = 'lm-word';
      outer.setAttribute('aria-hidden', 'true');
      outer.style.setProperty('--lm-word', index);
      inner.textContent = word;
      outer.append(inner);
      heroHeading.append(outer);
      if (index < words.length - 1) heroHeading.append(' ');
    });
    heroHost.classList.add('lm-words-host');
  }

  root.classList.add('lm-js');

  /* ----- 1. Scroll-Reveals ----- */

  const candidates = [];
  const add = (selector, type, resolve) => {
    main.querySelectorAll(selector).forEach((node) => {
      const el = resolve ? resolve(node) : node;
      if (!el || el.closest('.hero, .liora-soft-club')) return;
      candidates.push([el, type]);
    });
  };

  add('.media-with-content .media-block', 'media');
  add('.media-with-content__content', 'rise');
  add('liora-fabric-wheel', 'wheel');
  add('.collection-card', 'tile');
  add('product-card', 'card', (node) => {
    const parent = node.parentElement;
    const isItem = parent && (parent.tagName === 'LI' || parent.classList.contains('resource-list__item'));
    return isItem ? parent : node;
  });
  add('.shopify-section h2', 'heading', (node) => (node.classList.contains('visually-hidden') ? null : node));
  add('.text-block', 'rise', (node) => (node.querySelector('h1, h2, h3') ? null : node));
  add('.shopify-section form[action*="contact"]', 'rise');

  const fold = window.innerHeight * 0.92;
  const elements = [];

  candidates.forEach(([el, type]) => {
    if (el.hasAttribute('data-lm')) return;
    if (el.parentElement && el.parentElement.closest('[data-lm]')) return;
    if (el.getBoundingClientRect().top <= fold) return;
    el.setAttribute('data-lm', type);
    elements.push(el);
  });

  const pending = new Set(elements);

  const finish = (el) => {
    el.removeAttribute('data-lm');
    el.classList.remove('is-in');
    el.style.removeProperty('--lm-delay');
  };

  const revealObserver = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

      visible.forEach((entry, index) => {
        reveal(entry.target, Math.min(index, CONFIG.maxStaggerSteps) * CONFIG.stagger);
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
  );

  const reveal = (el, delay) => {
    if (!pending.has(el)) return;
    pending.delete(el);
    revealObserver.unobserve(el);
    el.style.setProperty('--lm-delay', delay + 'ms');
    el.classList.add('is-in');
    window.setTimeout(() => finish(el), 2800 + delay);
  };

  elements.forEach((el) => revealObserver.observe(el));

  /* Sicherheitsnetz: Was nach dem Scrollen im Bild liegt, aber vom
     Observer nicht gemeldet wurde, wird trotzdem eingeblendet. */
  const rescueTimer = window.setInterval(() => {
    if (!pending.size) {
      window.clearInterval(rescueTimer);
      return;
    }
    const vh = window.innerHeight;
    pending.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.height > 0 && rect.top < vh * 0.9 && rect.bottom > 0) reveal(el, 0);
    });
  }, 1200);

  /* ----- 2. Parallax + Scroll-Status ----- */

  const items = CONFIG.parallax
    ? [...main.querySelectorAll('.media-with-content')]
        .map((section) => {
          const media = section.querySelector('.media-block');
          return media ? { section, media, amp: 0 } : null;
        })
        .filter(Boolean)
    : [];

  const measure = () => {
    items.forEach((item) => {
      item.amp = Math.min(48, item.media.offsetHeight * 0.04);
    });
  };

  let ticking = false;

  const update = () => {
    ticking = false;
    root.classList.toggle('lm-scrolled', window.scrollY > 40);

    const vh = window.innerHeight;
    items.forEach(({ section, media, amp }) => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const shift = Math.max(-1, Math.min(1, progress)) * -amp;
      media.style.setProperty('--lm-shift', shift.toFixed(1) + 'px');
    });
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
      requestUpdate();
    },
    { passive: true }
  );
  window.addEventListener('load', () => {
    measure();
    requestUpdate();
  });
  measure();
  update();

  /* ----- 3. Stoff-Rad dreht von selbst weiter ----- */

  const wheel = main.querySelector('liora-fabric-wheel');

  if (wheel && CONFIG.wheelAutoplay) {
    let inView = false;
    let hovering = false;
    let pausedUntil = 0;

    new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
      },
      { threshold: 0.35 }
    ).observe(wheel);

    const pause = () => {
      pausedUntil = Date.now() + CONFIG.wheelPauseAfterInteraction;
    };

    wheel.addEventListener('pointerenter', () => {
      hovering = true;
    });
    wheel.addEventListener('pointerleave', () => {
      hovering = false;
    });
    ['pointerdown', 'click', 'keydown', 'focusin'].forEach((type) => wheel.addEventListener(type, pause));

    window.setInterval(() => {
      if (!inView || hovering || document.hidden || Date.now() < pausedUntil) return;
      if (typeof wheel.move === 'function') wheel.move(1);
    }, CONFIG.wheelInterval);
  }
  /* ----- 4. Hero: Atelier-Monogramm (snippets/liora-monogram.liquid) ----- */

  const heroContainer = main.querySelector('.hero-wrapper .hero__container, .hero .hero__container');
  const monogramTemplate = document.getElementById('LioraMonogramTemplate');

  if (heroContainer && monogramTemplate) {
    const seal = monogramTemplate.content.firstElementChild.cloneNode(true);
    heroContainer.append(seal);

    new IntersectionObserver(([entry]) => {
      seal.classList.toggle('is-paused', !entry.isIntersecting);
    }).observe(heroContainer);
  }

  /* ----- 5. Marquee: schneller beim Scrollen ----- */

  const marquee = main.querySelector('.liora-soft-club');
  const marqueeTrack = marquee && marquee.querySelector('.liora-soft-club__track');
  const marqueeAnimation = marqueeTrack && marqueeTrack.getAnimations ? marqueeTrack.getAnimations()[0] : null;

  if (marqueeAnimation) {
    let lastY = window.scrollY;
    let lastTime = performance.now();
    let rate = 1;
    let target = 1;
    let running = false;

    const step = () => {
      rate += (target - rate) * 0.12;
      target += (1 - target) * 0.08;
      marqueeAnimation.playbackRate = rate;
      marquee.style.setProperty('--lm-skew', ((rate - 1) * -1.6).toFixed(2) + 'deg');
      if (Math.abs(rate - 1) > 0.01 || Math.abs(target - 1) > 0.01) {
        window.requestAnimationFrame(step);
      } else {
        rate = 1;
        marqueeAnimation.playbackRate = 1;
        marquee.style.setProperty('--lm-skew', '0deg');
        running = false;
      }
    };

    window.addEventListener(
      'scroll',
      () => {
        const now = performance.now();
        const velocity = Math.abs(window.scrollY - lastY) / Math.max(16, now - lastTime);
        lastY = window.scrollY;
        lastTime = now;
        target = Math.min(CONFIG.marqueeMaxRate, Math.max(target, 1 + velocity * CONFIG.marqueeBoost * 60));
        if (!running) {
          running = true;
          window.requestAnimationFrame(step);
        }
      },
      { passive: true }
    );
  }

  /* ----- 6. Atelier: Naht schliesst sich beim Scrollen ----- */

  const seams = [...main.querySelectorAll('.media-with-content .media-with-content__content')].map((content) => {
    const frame = document.createElement('span');
    frame.className = 'lm-seam-frame';
    frame.setAttribute('aria-hidden', 'true');
    frame.innerHTML = '<i></i><i></i><i></i><i></i>';
    content.classList.add('lm-seam-host');
    content.append(frame);
    return { content, frame };
  });

  const updateSeams = () => {
    const vh = window.innerHeight;
    seams.forEach(({ content, frame }) => {
      const rect = content.getBoundingClientRect();
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      const progress = Math.max(0, Math.min(1, (vh * 0.9 - rect.top) / (rect.height * 0.75 + vh * 0.25)));
      for (let edge = 0; edge < 4; edge += 1) {
        const value = Math.max(0, Math.min(1, progress * 4 - edge));
        frame.style.setProperty('--lm-e' + (edge + 1), value.toFixed(3));
      }
    });
  };

  if (seams.length) {
    window.addEventListener('scroll', () => window.requestAnimationFrame(updateSeams), { passive: true });
    window.addEventListener('resize', updateSeams, { passive: true });
    updateSeams();
  }

  /* ----- 7. Newsletter: Poststempel aus Heidelberg ----- */

  const signup = main.querySelector('.email-signup-block');
  const signupSection = signup && signup.closest('.shopify-section');
  const signupHeading = signupSection && signupSection.querySelector('h2');

  if (signupHeading) {
    const stamp = svg(
      `<svg viewBox="0 0 200 84">
        <defs><path id="lm-stamp-path" d="M42 42m-31 0a31 31 0 1 1 62 0a31 31 0 1 1-62 0"/></defs>
        <circle class="lm-stamp__ring" cx="42" cy="42" r="38" pathLength="1"/>
        <circle class="lm-stamp__ring lm-stamp__ring--inner" cx="42" cy="42" r="24" pathLength="1"/>
        <text class="lm-stamp__text"><textPath href="#lm-stamp-path" textLength="190">HEIDELBERG · LIORA · POST ·</textPath></text>
        <path class="lm-stamp__heart" d="M42 50c-7-5-11-8.5-11-13a5.5 5.5 0 0 1 11-1 5.5 5.5 0 0 1 11 1c0 4.5-4 8-11 13Z" pathLength="1"/>
        <path class="lm-stamp__wave" style="--lm-wave:0" d="M88 26c9-7 18 7 27 0s18 7 27 0 18 7 27 0 18 7 27 0" pathLength="1"/>
        <path class="lm-stamp__wave" style="--lm-wave:1" d="M88 42c9-7 18 7 27 0s18 7 27 0 18 7 27 0 18 7 27 0" pathLength="1"/>
        <path class="lm-stamp__wave" style="--lm-wave:2" d="M88 58c9-7 18 7 27 0s18 7 27 0 18 7 27 0 18 7 27 0" pathLength="1"/>
      </svg>`,
      'lm-stamp'
    );
    const holder = document.createElement('div');
    holder.className = 'lm-stamp-holder';
    holder.append(stamp);
    const headingBlock = signupHeading.closest('.text-block') || signupHeading;
    headingBlock.before(holder);

    new IntersectionObserver(
      ([entry], observer) => {
        if (!entry.isIntersecting) return;
        holder.classList.add('is-in');
        observer.disconnect();
      },
      { threshold: 0.6 }
    ).observe(holder);
  }
})();
